import { Component, ElementRef, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { Subscription } from 'rxjs/internal/Subscription';

import { SsModuleService } from '@app/services';

@Component({
  selector: 'module-type-select-dialog',
  templateUrl: './module-type-select-dialog.component.html',
  styleUrls: ['./module-type-select-dialog.component.scss'],
  standalone: false
})
export class ModuleTypeSelectDialogComponent implements OnInit, OnDestroy {
  acceptedModules: string[] = [];
  multiSelect: boolean = false;
  title: string = 'Select a Type';
  placeholder: string = 'Select a type';

  modules: any[] = [];
  selection: any | any[] = null;

  open: boolean = false;
  searchTerm: string = '';
  hiddenIndices: Set<number> = new Set<number>();

  loading: boolean = true;

  @ViewChild('triggerEl') triggerEl: ElementRef<HTMLElement>;
  @ViewChild('panelTemplate') panelTemplate: TemplateRef<any>;
  @ViewChild('searchInput') searchInputRef: ElementRef<HTMLInputElement>;

  private _overlayRef: OverlayRef | null = null;
  private _outsideClickSub: Subscription | null = null;

  constructor(
    private _dialogRef: MatDialogRef<ModuleTypeSelectDialogComponent>,
    private _ssModuleService: SsModuleService,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef
  ) {}


  async ngOnInit() {
    try {
      if (!this._ssModuleService.allModules) {
        this._ssModuleService.allModules = await lastValueFrom(this._ssModuleService.getAll());
      }
      const all = this._ssModuleService.allModules || [];
      const accepted = this.acceptedModules || [];
      const filtered = accepted.length ? all.filter(m => accepted.includes(m?.name)) : all.slice();
      this.modules = filtered.sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
      if (this.multiSelect) this.selection = [];
    } catch (err) {
      console.log('Error loading modules in module-type-select-dialog: ', err);
      this.modules = [];
    } finally {
      this.loading = false;
    }
  }


  ngOnDestroy() {
    this._teardownOverlay();
  }


  togglePanel() {
    if (this.open) this.closePanel();
    else this.openPanel();
  }


  openPanel() {
    if (this._overlayRef) return;
    const triggerEl = this.triggerEl?.nativeElement;
    if (!triggerEl || !this.panelTemplate) return;

    const positionStrategy = this._overlay.position()
      .flexibleConnectedTo(triggerEl)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      width: triggerEl.offsetWidth,
      hasBackdrop: false
    });

    this._overlayRef.attach(new TemplatePortal(this.panelTemplate, this._viewContainerRef));
    this.open = true;

    setTimeout(() => this.searchInputRef?.nativeElement?.focus(), 0);

    this._outsideClickSub = fromEvent<MouseEvent>(document, 'click').subscribe(event => {
      const target = event.target as Node;
      if (this._overlayRef?.overlayElement?.contains(target)) return;
      if (triggerEl.contains(target)) return;
      this.closePanel();
    });
  }


  closePanel() {
    this._teardownOverlay();
    this.open = false;
    this.searchTerm = '';
    this.hiddenIndices = new Set<number>();
  }


  private _teardownOverlay() {
    this._outsideClickSub?.unsubscribe();
    this._outsideClickSub = null;
    if (this._overlayRef) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }
  }


  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.open) this.closePanel();
    else this.onCancel();
  }


  applySearchFilter() {
    const term = this.searchTerm?.toLowerCase().trim() || '';
    if (!term?.length) {
      this.hiddenIndices = new Set<number>();
      return;
    }
    const next = new Set<number>();
    const opts = this.modules || [];
    for (let i = 0; i < opts.length; i++) {
      const optLabel = String(opts[i]?.label || '').toLowerCase();
      if (!optLabel.includes(term)) next.add(i);
    }
    this.hiddenIndices = next;
  }


  isSelected(option: any): boolean {
    if (!option) return false;
    if (this.multiSelect) {
      const arr = Array.isArray(this.selection) ? this.selection : [];
      return arr.some(m => m?._id === option?._id);
    }
    return this.selection?._id === option?._id;
  }


  onOptionClick(option: any, event: MouseEvent) {
    event?.stopPropagation();
    if (this.multiSelect) {
      this._toggleMulti(option);
      return;
    }
    this.selection = option;
    this.closePanel();
  }


  private _toggleMulti(option: any) {
    const arr = Array.isArray(this.selection) ? [...this.selection] : [];
    const idx = arr.findIndex(m => m?._id === option?._id);
    if (idx >= 0) arr.splice(idx, 1);
    else arr.push(option);
    this.selection = arr;
  }


  removeFromSelection(option: any, event: MouseEvent) {
    event?.stopPropagation();
    if (this.multiSelect) {
      const arr = Array.isArray(this.selection) ? [...this.selection] : [];
      const idx = arr.findIndex(m => m?._id === option?._id);
      if (idx >= 0) arr.splice(idx, 1);
      this.selection = arr;
      return;
    }
    this.selection = null;
  }


  selectionAsArray(): any[] {
    if (!this.selection) return [];
    if (this.multiSelect) return Array.isArray(this.selection) ? this.selection : [];
    return [this.selection];
  }


  triggerText(): string {
    if (this.multiSelect) {
      const len = this.selection?.length || 0;
      return len ? `${len} selected` : this.placeholder;
    }
    return this.selection?.label || this.placeholder;
  }


  hasSelection(): boolean {
    if (this.multiSelect) return !!this.selection?.length;
    return this.selection != null;
  }


  showSelectedChips(): boolean {
    if (this.multiSelect) return !!this.selection?.length;
    return this.selection != null;
  }


  allHiddenByFilter(): boolean {
    const total = this.modules?.length || 0;
    if (!total) return false;
    return this.hiddenIndices.size === total;
  }


  selectButtonSuffix(): string {
    if (!this.multiSelect) return '';
    const len = this.selection?.length || 0;
    return len ? ` (${len})` : '';
  }


  onCancel() {
    this._dialogRef.close(undefined);
  }


  onConfirm() {
    if (!this.hasSelection()) return;
    this._dialogRef.close(this.selection);
  }
}